/**
 * `GET /bql/fields`, parsed per source and per field rather than
 * all-or-nothing.
 *
 * `FieldsSectionSchema`'s `success` union is already the fix for the shape
 * that USED to be hand-tightened here (see `envelope.ts`'s header) — this
 * module is the other half: a SUCCESSFUL section's `fields` array can still
 * carry one `FieldDescriptor` this client's generated `FieldTypeSchema` or
 * `OperatorSchema` does not recognise, and under an atomic parse that field
 * cost the WHOLE section, sinking every field discovered for that source.
 *
 * ## The envelope is loosened in exactly two places
 *
 * `sources` is loosened to `Record<string, unknown>` so each source can be
 * re-gated on its own, and a SUCCESSFUL section's `fields` is loosened to
 * `unknown[]` so each field can be re-gated on its own. A FAILED section
 * (`FieldsSectionFailureSchema`) is left exactly as generated-strict — its
 * `error.code` is the same four-value enum `batch-contract.ts` already
 * treats as additive-drift surface, and re-gating it here would be a second
 * definition of the same rule.
 *
 * A dropped field needs no new UI, by the same reasoning as a dropped BQL
 * schema field (see `schema-contract.ts`): it simply is not in the section's
 * `fields`, indistinguishable from a field the server never discovered.
 *
 * **Flagged, not fixed (out of this item's scope):** a discovered
 * `FieldDescriptor`'s `label` (and `description`, where present) is a
 * VENDOR-controlled free string — Okta's, AWS's, whichever integration's own
 * field naming — not an enum this client tracks, so there is nothing here for
 * a partial parse to gate. `id`/`type`/`operators` are the enum surface this
 * module protects; `label`/`description` are just `z.string()`.
 */
import { z } from 'zod'

import {
	parseArrayRows,
	parseRecordRows,
	rowKeyOf,
} from '@/shared/api/utils/partial-contract'

import { BqlContractError, parseResponse } from './contract'
import {
	FieldDescriptorSchema,
	FieldsSectionFailureSchema,
	FieldsSectionSuccessSchema,
} from './generated/schema'

import type { FieldsResponse, FieldsSection } from './generated/schema'
import type { DroppedRow } from '@/shared/api/utils/partial-contract'

/** `FieldsSectionSuccessSchema`, with `fields` loosened for per-field re-gating. */
const fieldsSectionSuccessEnvelopeSchema = FieldsSectionSuccessSchema.extend({
	fields: z.array(z.unknown()),
})

/** `FieldsSectionSchema`, with a successful section's `fields` loosened; a failed section stays generated-strict. */
const fieldsSectionEnvelopeSchema = z.discriminatedUnion('success', [
	fieldsSectionSuccessEnvelopeSchema,
	FieldsSectionFailureSchema,
])

/** `FieldsResponseSchema`, with `sources`' values loosened for per-source re-gating. */
const fieldsResponseEnvelopeSchema = z.object({
	sources: z.record(z.string(), z.unknown()),
})

/** A `FieldDescriptor`'s `id` when the row is at least shaped enough to have one, else its array index. */
const fieldRowKey = rowKeyOf('id')

/**
 * Parse `GET /bql/fields` with per-source and per-field blast radius: a
 * source, or one field within an otherwise-successful source, that fails its
 * own schema is dropped and reported; every sibling source and field still
 * renders.
 */
export function parseFieldsResponse(
	body: unknown,
	route: string,
): { value: FieldsResponse; dropped: DroppedRow[] } {
	const envelope = parseResponse(fieldsResponseEnvelopeSchema, body, route)
	const dropped: DroppedRow[] = []

	const sectionsEnvelope = parseRecordRows(
		envelope.sources,
		fieldsSectionEnvelopeSchema,
		(key, cause) => new BqlContractError(`${route} sources.${key}`, cause),
	)
	dropped.push(
		...sectionsEnvelope.dropped.map((row) => ({
			...row,
			key: `sources.${row.key}`,
		})),
	)

	const sources: Record<string, FieldsSection> = {}

	for (const [key, section] of Object.entries(sectionsEnvelope.value)) {
		if (!section.success) {
			sources[key] = section
			continue
		}

		const fields = parseArrayRows(
			section.fields,
			FieldDescriptorSchema,
			(fieldKey, cause) =>
				new BqlContractError(
					`${route} sources.${key}.fields.${fieldKey}`,
					cause,
				),
			fieldRowKey,
		)
		dropped.push(
			...fields.dropped.map((row) => ({
				...row,
				key: `sources.${key}.fields.${row.key}`,
			})),
		)

		sources[key] = { ...section, fields: fields.value }
	}

	return { value: { sources }, dropped }
}
